//---------------------------------------------------------------------------------------------------
// ## IMPORTS ##
// Library Imports
// Material ui Imports
// Custom components imports (and hooks and helpers)
import { useEffect } from "react";
import { VTButton } from "@virtus-tech-repository/virtus-tech-repository";
import { EDraggableOptions } from "@virtus-tech-repository/virtus-tech-repository/lib/models/media.model";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { setCurrentDragging, setCurrentSidePanel } from "../../store/slices/current.slice";
import VTCreatorTabs, { getIsOnEditMarksPage } from "../components/VTCreatorTabs";
import { Paper, useMediaQuery, useTheme } from "@mui/material";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import VTGroupsSidePanel from "../components/VTGroupsSidePanel";
import VTUsersSidePanel from "../components/VTUsersSidePanel";
import VTAttemptsSidePanel from "../components/VTAttemptsSidePanel";
import { useLocation } from "react-router-dom";
import VTVersionSidePanel from "../components/VTVersionSidePanel";
// Media imports

//---------------------------------------------------------------------------------------------------
// ## MUI STYLED ##
// Layout stuff remains inline, other styling pulled into it's own components

//---------------------------------------------------------------------------------------------------
// ## INTERFACES ##
interface IProps {
    videoCurrentState: "Paused" | "Playing" | "Question" | undefined;
    setVideoCurrentState: (videoCurrentState: "Paused" | "Playing" | "Question" | undefined) => void;
}

//---------------------------------------------------------------------------------------------------
// ## COMPONENTS ##
// Only display layouts in pages
export default function CreatorTabsPanelContainer({ videoCurrentState, setVideoCurrentState }: IProps) {
    //-------------------------------------------------------------------------------------------------
    // ## HOOKS ##
    const dispatch = useAppDispatch();
    const theme = useTheme();

    //-------------------------------------------------------------------------------------------------
    // ## USE SELECTOR ##
    const {
        currentSidePanel,
        editHotspotDialogIsCurrentlyOpen,
        editHotspotDialogTabCurrentlyOpen,
        currentHotspot,
        currentlyPreview,
        currentSidePannelDesiredWidth,
    } = useAppSelector((state) => state.currentReducer);

    //-------------------------------------------------------------------------------------------------
    // ## USE STATE ##

    const matches = useMediaQuery("(min-width:1200px)");

    //-------------------------------------------------------------------------------------------------
    // ## USE EFFECTS ##
    useEffect(() => {
        if (editHotspotDialogTabCurrentlyOpen === "content" && currentSidePanel.currentTab) {
            setOpenDraw("Images");
        }
    }, [editHotspotDialogTabCurrentlyOpen]);

    //-------------------------------------------------------------------------------------------------
    // ## CUSTOM FUNCTIONS ##
    // Function to handle closing the draw via tab button
    function setCloseDraw() {
        dispatch(
            setCurrentSidePanel({
                currentTab: undefined,
                currentWidth: 0,
                option: undefined,
            }),
        );
    }

    // Function to handle opening the draw via tab button
    function setOpenDraw(drawName: string) {
        dispatch(
            setCurrentSidePanel({
                currentTab: drawName,
                currentWidth: currentSidePannelDesiredWidth,
                option: undefined,
            }),
        );
    }

    // Hook to hold the current drag item data
    function handleDragStart(mediaType: EDraggableOptions, src: string, preview?: string | undefined) {
        dispatch(
            setCurrentDragging({
                mediaType: mediaType,
                src: src,
                preview: preview,
            }),
        );
    }

    // Hook to clear the current drag item data
    function handleDragStop() {
        dispatch(setCurrentDragging(undefined));
    }

    const location = useLocation();

    const isOnEditMarksPage = getIsOnEditMarksPage(location.pathname);

    useEffect(() => {
        if (isOnEditMarksPage) {
            setCloseDraw();
        }
    }, [isOnEditMarksPage]);

    return (
        <>
            {/*-----------------------------------------------------------------------------------------*/}
            {/* Tab sections */}
            <VTCreatorTabs
                currentEditHotSpotDialogTabOpen={editHotspotDialogTabCurrentlyOpen}
                isEditHotSpotDialogOpen={editHotspotDialogIsCurrentlyOpen}
                setCloseDraw={setCloseDraw}
                setOpenDraw={setOpenDraw}
                disabled={["template", "video"]}
            />

            {/*-----------------------------------------------------------------------------------------*/}
            {/* Side panel with media content */}

            <Paper
                sx={{
                    position: "absolute",
                    top: "68px",
                    left: "80px",
                    bottom: "0px",
                    width: currentSidePanel.currentWidth,
                    zIndex: 1440,
                    borderRadius: 0,
                    // TODO: Not sure why the background was changing color??
                    // backgroundColor: !currentSidePanel.currentWidth ? "#171717" : "#444547",
                    backgroundColor: theme.palette.background.paper,
                    // transition: "0.1s",
                }}
            >
                {currentSidePanel.currentWidth !== 0 && (
                    <VTButton
                        type={"icon"}
                        toolTipOptions={{
                            toolTipPlacement: "top-end",
                            toolTipText: "Collapse",
                        }}
                        sx={{
                            position: "absolute",
                            left: currentSidePanel.currentWidth,
                            top: currentHotspot.currentHotspot ? "80px" : "5.5px",
                            width: "30px",
                            height: "30px",
                            borderRadius: "0px 15px 15px 0px",
                            zIndex: 10,
                            backgroundColor: "#444547",
                            fontSize: "0.3em",
                            ":hover": {
                                cursor: "pointer",
                                backgroundColor: "#646567",
                            },
                        }}
                        onClick={() =>
                            dispatch(
                                setCurrentSidePanel({
                                    currentTab: undefined,
                                    currentWidth: 0,
                                    option: undefined,
                                }),
                            )
                        }
                    >
                        <CloseRoundedIcon sx={{ fontSize: "3.5em", marginLeft: "-4px" }} />
                    </VTButton>
                )}
                {/*-------------------------------------------------------------------------------------*/}
                {/* Template panel */}
                {currentSidePanel.currentTab === "Groups" && <VTGroupsSidePanel />}

                {currentSidePanel.currentTab === "Users" && <VTUsersSidePanel />}

                {currentSidePanel.currentTab === "Attempts" && <VTAttemptsSidePanel />}

                {currentSidePanel.currentTab === "Version" && <VTVersionSidePanel />}
            </Paper>
        </>
    );
}
